import axios from "axios";
import { setOrgList, getActiveLegalUnitId } from "./globalData";

const setTokenInfo = (data) => {
  const dataToStr = JSON.stringify(data);
  sessionStorage.setItem("sty-user-token", dataToStr);
};
export const getTokenInfo = () => {
  const data = sessionStorage.getItem("sty-user-token");
  return data ? JSON.parse(data) : {};
};
const setUsrBearerToken = (data) => {
  data && sessionStorage.setItem("sty-bearer-token", data);
};

const getUsrBearerToken = () => {
  return sessionStorage.getItem("sty-bearer-token");
};


/**
 * The legal entity the user is working in, sent with every request.
 *
 * The server has no other way to know. Tenancy is per organisation, and an
 * organisation exists so that several entities can share one account — so
 * "which am I looking at" is a property of the request. A header rather than a
 * query parameter because otherwise every list endpoint grows one and they
 * drift.
 *
 * Omitted when there is no active entity, which is what an organisation with
 * none sends and what the server reads as "no narrowing". The server verifies
 * the id against the caller's organisation regardless — a header is
 * caller-controlled and trusting one would be the same hole the entity-scoped
 * department read had.
 *
 * X-Legal-Unit must stay in the backend's CORS allowedHeaders. It is an
 * allow-list: a header missing from it is dropped by the browser's preflight,
 * and the request simply arrives unscoped with nothing to see in the console.
 */
const withLegalUnit = (headers = {}) => {
  const entityId = getActiveLegalUnitId();
  return entityId ? { ...headers, "X-Legal-Unit": entityId } : headers;
};

export let org_id;
export const setOrgId = (id) => (org_id = id);

const getUrl = (path) => {
  // Optional override, for pointing the app at a backend running locally.
  // Set REACT_APP_API_URL in .env.development.local (gitignored) rather than
  // editing this function — that file never reaches a commit.
  //   REACT_APP_API_URL=http://localhost:5050/api
  // process.env should be disabled for linting
  // eslint-disable-next-line
  const apiBase = process.env.REACT_APP_API_URL;
  if (apiBase) {
    return `${apiBase.replace(/\/$/, "")}${path}`;
  }
  // eslint-disable-next-line
  if (process.env.REACT_APP_MODE === "production") {
    return `https://api.payloom.in/api${path}`;
  }
  return `https://dev.payloom.in/api${path}`;
};

const storeTokenInfo = (data = {}) => {
  const { accessToken, accessTokenData, session, organizations } = data;
  // The access-token response carries the organisations this person can switch
  // to. Taking it here means the switcher is refreshed on every load and every
  // refresh, rather than holding whatever was true when they signed in — which
  // left deleted organisations in the menu.
  if (Array.isArray(organizations)) setOrgList(organizations);
  let tokenInfo = getTokenInfo();
  if (accessToken) {
    tokenInfo = { ...tokenInfo, accessToken };
  }
  if (accessTokenData) {
    tokenInfo = { ...tokenInfo, accessTokenData };
  }
  // `session` is the expiry only. The refresh token itself is never sent here
  // any more — it lives in an httpOnly cookie, which is the point: it used to
  // arrive in the body as well and get written to sessionStorage, so any script
  // on the page could read a credential valid for two months.
  if (session) {
    tokenInfo = { ...tokenInfo, session };
  }
  setTokenInfo(tokenInfo);
  // Only an access token is ever a bearer. It is short-lived, and there is no
  // longer a long-lived secret in storage to fall back to.
  setUsrBearerToken(tokenInfo.accessToken);
};

export const setBearerToken = async () => {
  const tokenInfo = getTokenInfo() || {};
  // `exp` is a JWT claim, so it is UNIX seconds — not milliseconds, which is
  // what new Date() would read it as. Refresh two minutes early so a request
  // never carries an about-to-expire token; with no token at all, refresh.
  const { exp } = tokenInfo.accessTokenData || {};
  const stillValid = exp && exp > Math.floor(Date.now() / 1000) + 120;
  if (stillValid) {
    return true;
  } else if (org_id) {
    const request = {
      method: "GET",
      withCredentials: true,
      url: getUrl(`/auth/access-token/${org_id}`),
      headers: {
        "Content-Type": "application/json",
      },
    };

    try {
      const res = await axios.request(request);
      const { data } = res.data;
      storeTokenInfo(data);
      return data;
    } catch (err) {
      const { message, response = {} } = err;
      if (response.data) return response.data;
      return {
      success: false,
      data: null,
      message,
    };
    }
  }
};

export const postCall = async (req) => {
  const { firebaseToken, path, bodyParams = {} } = req;
  const requestOptions = {
    method: "POST",
    withCredentials: true,
    data: {
      ...bodyParams,
    },
    url: getUrl(path),
    headers: withLegalUnit({
      "Content-Type": "application/json",
    }),
  };

  if (firebaseToken) {
    requestOptions.data["firebaseToken"] = firebaseToken;
  }

  let bearerToken = getUsrBearerToken();
  if (bearerToken && bearerToken !== "undefined") {
    await setBearerToken();
    bearerToken = getUsrBearerToken();
    requestOptions.headers["Authorization"] = `Bearer ${bearerToken}`;
  }

  try {
    const res = await axios.request(requestOptions);
    const { data, message } = res.data;
    storeTokenInfo(data);
    return {
      success: true,
      data,
      message,
    };
  } catch (err) {
    const { message, response = {} } = err;
    if (response.data) {
      // `details` carries express-validation's field-level errors and was
      // being dropped here, in every call helper — which is why a form could
      // only ever say "Validation Failed" and highlight nothing. Passed
      // through so callers can point at the field the server objected to.
      const { data, message, details, code } = response.data;
      return {
        success: false,
        // The HTTP status and the server's own code, as getCall already
        // returned. Without them a caller cannot tell "you may not" from "it
        // broke" — the login screen needs exactly that to know an unverified
        // address from a wrong password.
        status: response.status,
        code,
        data,
        message,
        details,
      };
    }
    return {
      success: false,
      data: null,
      message,
    };
  }
};

export const putCall = async (req) => {
  const { firebaseToken, path, bodyParams = {} } = req;
  const requestOptions = {
    method: "PUT",
    withCredentials: true,
    data: {
      ...bodyParams,
    },
    url: getUrl(path),
    headers: withLegalUnit({
      "Content-Type": "application/json",
    }),
  };

  if (firebaseToken) {
    requestOptions.data["firebaseToken"] = firebaseToken;
  }

  let bearerToken = getUsrBearerToken();
  if (bearerToken && bearerToken !== "undefined") {
    await setBearerToken();
    bearerToken = getUsrBearerToken();
    requestOptions.headers["Authorization"] = `Bearer ${bearerToken}`;
  }

  try {
    const res = await axios.request(requestOptions);
    const { data, message } = res.data;
    storeTokenInfo(data);
    return {
      success: true,
      data,
      message,
    };
  } catch (err) {
    const { message, response = {} } = err;
    if (response.data) {
      // `details` carries express-validation's field-level errors and was
      // being dropped here, in every call helper — which is why a form could
      // only ever say "Validation Failed" and highlight nothing. Passed
      // through so callers can point at the field the server objected to.
      const { data, message, details, code } = response.data;
      return {
        success: false,
        data,
        message,
        details,
        // The server's own reason code. A tenant route can answer org_pending
        // or org_rejected, and a screen cannot tell "your organisation is being
        // reviewed" from a generic failure without it.
        code,
      };
    }
    return {
      success: false,
      data: null,
      message,
    };
  }
};

export const deleteCall = async (req) => {
  const { path, bodyParams = {} } = req;
  const requestOptions = {
    method: "DELETE",
    withCredentials: true,
    data: { ...bodyParams },
    url: getUrl(path),
    headers: withLegalUnit({
      "Content-Type": "application/json",
    }),
  };

  let bearerToken = getUsrBearerToken();
  if (bearerToken && bearerToken !== "undefined") {
    await setBearerToken();
    bearerToken = getUsrBearerToken();
    requestOptions.headers["Authorization"] = `Bearer ${bearerToken}`;
  }

  try {
    const res = await axios.request(requestOptions);
    const { data, message } = res.data;
    storeTokenInfo(data);
    return { success: true, data, message };
  } catch (err) {
    const { message, response = {} } = err;
    if (response.data) {
      // see the note on the other error branches: details is what tells a form
      // which field the server refused
      const { data, message, details, code } = response.data;
      // The HTTP status, so a caller can tell "you may not" from "it broke".
      // Without it every failure looks the same and a screen that wants to fall
      // back on a 403 has to guess from the message text.
      return { success: false, status: response.status, data, message, details, code };
    }
    return { success: false, status: response.status, data: null, message };
  }
};

/**
 * GET a file the server generated, as a Blob.
 *
 * Separate from getCall because that one unwraps res.data.data as JSON, which
 * turns a CSV body into undefined. Used where the file must be built on the
 * server rather than in the browser — a bank advice is assembled from account
 * numbers, and those should not have to reach the page to produce it.
 *
 * An error body still arrives as a Blob, so it is read back as text to recover
 * the server's message instead of reporting a generic failure.
 */
export const downloadCall = async (req) => {
  // `method` because issuing a letter is a POST — it records that the letter
  // was issued — while still returning the file itself. Defaulted to GET, so
  // the existing callers that only read a file are unchanged.
  const { path, method = "GET" } = req;
  const requestOptions = {
    method,
    withCredentials: true,
    responseType: "blob",
    url: getUrl(path),
    headers: withLegalUnit(),
  };

  const bearerToken = getUsrBearerToken();
  if (bearerToken && bearerToken !== "undefined") {
    await setBearerToken();
    requestOptions.headers = { Authorization: `Bearer ${getUsrBearerToken()}` };
  }

  try {
    const res = await axios.request(requestOptions);
    return { success: true, blob: res.data, filename: filenameFrom(res.headers) };
  } catch (err) {
    let message = err?.message;
    const body = err?.response?.data;
    if (body instanceof Blob) {
      try {
        const parsed = JSON.parse(await body.text());
        message = parsed?.message || message;
      } catch {
        /* not JSON — keep the transport error */
      }
    }
    return { success: false, blob: null, message };
  }
};

// Content-Disposition carries the name the server chose; falling back to null
// lets the caller name it rather than inventing one here.
const filenameFrom = (headers = {}) => {
  const header = headers["content-disposition"] || headers["Content-Disposition"];
  const match = header && header.match(/filename="?([^"';]+)"?/i);
  return match ? match[1] : null;
};

export const getCall = async (req, type) => {
  const { path } = req;
  const requestOptions = {
    method: "GET",
    withCredentials: true,
    url: getUrl(path),
    headers: withLegalUnit({
      "Content-Type": "application/json",
    }),
  };

  // OrgLogin is the access-token request itself, and that endpoint authenticates
  // with the REFRESH token — which now lives only in the httpOnly cookie that
  // withCredentials already sends. It must carry no Authorization header at all:
  // sending the access token there gets it looked up as a refresh token and
  // refused, and sending the refresh token is what we just stopped storing.
  let bearerToken = getUsrBearerToken();
  if (type !== "OrgLogin" && bearerToken && bearerToken !== "undefined") {
    await setBearerToken();
    bearerToken = getUsrBearerToken();
    requestOptions.headers["Authorization"] = `Bearer ${bearerToken}`;
  }

  try {
    const res = await axios.request(requestOptions);
    const { data, message } = res.data;
    storeTokenInfo(data);
    return {
      success: true,
      data,
      message,
    };
  } catch (err) {
    const { message, response = {} } = err;
    if (response.data) {
      // `details` carries express-validation's field-level errors and was
      // being dropped here, in every call helper — which is why a form could
      // only ever say "Validation Failed" and highlight nothing. Passed
      // through so callers can point at the field the server objected to.
      const { data, message, details, code } = response.data;
      return {
        success: false,
        // The HTTP status, so a caller can tell "you may not" from "it broke".
        // Without it every failure looks alike, and a screen that wants to fall
        // back on a 403 has to guess from the message text.
        status: response.status,
        data,
        message,
        details,
        // The server's own reason code. A tenant route can answer org_pending
        // or org_rejected, and a screen cannot tell "your organisation is being
        // reviewed" from a generic failure without it.
        code,
      };
    }

    return {
      success: false,
      status: response.status,
      data: null,
      message,
    };
  }
};
